import { Chip } from "@heroui/react";
import { Building, Storefront, Cube } from "@phosphor-icons/react";
import { UnitType, UnitSubtype } from "shared/types/unit";

interface UnitIdLinkChipProps {
  unitId: string;
  unitType?: UnitType | string | null;
  unitSubtype?: UnitSubtype | string | null;
}

/**
 * Colour + icon per unit type so users scan the leegstand table at a glance:
 * - Residentieel → moss green (our default, matches the Yally brand).
 * - Commercieel → amber/orange (stands out against the residential majority).
 * - Overig / unknown → neutral gray.
 */
const getTypeStyle = (type: string | null | undefined) => {
  switch (type) {
    case "Commercieel":
      return {
        icon: Storefront,
        chipClass:
          "h-fit bg-white px-2 border border-amber-500 text-amber-700 hover:bg-amber-500/10 transition-colors cursor-pointer",
        subLabelClass: "text-amber-700",
      };
    case "Overig":
      return {
        icon: Cube,
        chipClass:
          "h-fit bg-white px-2 border border-neutral-primary/40 text-neutral-primary hover:bg-neutral-primary/5 transition-colors cursor-pointer",
        subLabelClass: "text-neutral-secondary-mid",
      };
    case "Residentieel":
    default:
      return {
        icon: Building,
        chipClass:
          "h-fit bg-white px-2 border border-moss-primary text-moss-primary hover:bg-moss-primary/10 transition-colors cursor-pointer",
        subLabelClass: "text-moss-primary/80",
      };
  }
};

/**
 * Unit chip used in leegstand-style tables — clicking opens the unit detail
 * page in a new tab. Renders the unit ID with a type-specific colour/icon and
 * (optionally) the type/subtype underneath for quick scanning.
 */
export const UnitIdLinkChip = ({
  unitId,
  unitType,
  unitSubtype,
}: UnitIdLinkChipProps) => {
  const { icon: Icon, chipClass, subLabelClass } = getTypeStyle(unitType ?? null);

  const subLine = [unitType, unitSubtype].filter(Boolean).join(" · ");

  return (
    <div className="flex flex-col gap-0.5">
      <a
        href={`/unit/detail/${unitId}`}
        target="_blank"
        rel="noopener noreferrer"
        title="Open unit in nieuw tabblad"
        className="inline-block"
      >
        <Chip
          startContent={<Icon weight="duotone" size={14} />}
          classNames={{
            base: chipClass,
            content: "px-0 font-medium",
          }}
        >
          {unitId}
        </Chip>
      </a>
      {subLine && (
        <span className={`pl-1 text-[11px] uppercase tracking-wide ${subLabelClass}`}>
          {subLine}
        </span>
      )}
    </div>
  );
};

export default UnitIdLinkChip;
